import { FC, ReactNode } from 'react';
import { useFragment, graphql, RelayProp } from 'react-relay';

import { SharedPagination } from '../../shared/SharedPagination/SharedPagination';
import { SbRespRefineMenuSelector } from '../SbRespRefineMenuSelector/SbRespRefineMenuSelector';
import { SbRespSortItems } from '../SbRespSortItems/SbRespSortItems';
import { SbRespPageSize } from '../SbRespPageSize/SbRespPageSize';
import { SbRespBrowseHeader } from '../SbRespBrowseHeader/SbRespBrowseHeader';
import { SbRespAppliedFilters } from '../SbRespAppliedFilters/SbRespAppliedFilters';
import { SbRespSearchResultContainer } from '../SbRespSearchResultContainer/SbRespSearchResultContainer';
import { SbSharedRelatedItems } from '../SbSharedRelatedItems/SbSharedRelatedItems';
import { SbSharedRecentlySoldItems } from '../SbSharedRecentlySoldItems/SbSharedRecentlySoldItems';
import { SbSharedNetPriceFilterSwitch } from '../SbSharedNetPriceFilterSwitch/SbSharedNetPriceFilterSwitch';

import { useSbSelector } from '../../reducers/useSbSelector';
import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext';
import { VERTICALS } from '../../constants/uriConstants';
import { pageTypeConstants as pageTypes } from '../../constants/pageTypeConstants';

import classnames from 'classnames';
import dibsCss from 'dibs-css';

import { SbRespMainContent_itemSearch$key } from './__generated__/SbRespMainContent_itemSearch.graphql';
import { SbRespMainContent_auctionsItemSearch$key } from './__generated__/SbRespMainContent_auctionsItemSearch.graphql';
import { SbRespMainContent_viewer$key } from './__generated__/SbRespMainContent_viewer.graphql';
import { SbRespMainContent_user$key } from './__generated__/SbRespMainContent_user.graphql';

const viewerFragment = graphql`
    fragment SbRespMainContent_viewer on Viewer {
        ...SbRespRefineMenuSelector_viewer
        ...SbRespSearchResultContainer_viewer
        ...SbSharedRelatedItems_viewer
        ...SbSharedRecentlySoldItems_viewer
    }
`;

const userFragment = graphql`
    fragment SbRespMainContent_user on User {
        canShowSellerName
        canShowInternalSellerName: hasPermission(name: "VIEW_SELLER_NAME")
        canShowStorefrontOnlyBadge: hasPermission(name: "VIEW_STOREFRONT_ONLY_BADGE")
        ...SbRespRefineMenuSelector_user
        ...SbRespSearchResultContainer_user
        ...SbRespAppliedFilters_user
        ...SbRespSortItems_user
    }
`;

const itemSearchFragment = graphql`
    fragment SbRespMainContent_itemSearch on ItemSearchQueryConnection {
        topCategoryL1
        displayUriRef
        pageType
        displayMaxNumberOfPages
        ...SbRespRefineMenuSelector_itemSearch
        ...SbRespSortItems_itemSearch
        ...SbRespPageSize_itemSearch
        ...SbRespBrowseHeader_itemSearch
        ...SbRespSearchResultContainer_itemSearch
        ...SbSharedRelatedItems_itemSearch
        ...SbSharedRecentlySoldItems_itemSearch
        ...SbRespAppliedFilters_itemSearch
        ...SbSharedNetPriceFilterSwitch_itemSearch
    }
`;

const auctionsItemSearchFragment = graphql`
    fragment SbRespMainContent_auctionsItemSearch on ItemSearchQueryConnection {
        ...SbRespSearchResultContainer_auctionsItemSearch
    }
`;

type Props = {
    currency: string;
    itemSearch: SbRespMainContent_itemSearch$key;
    auctionsItemSearch: SbRespMainContent_auctionsItemSearch$key;
    viewer: SbRespMainContent_viewer$key;
    user: SbRespMainContent_user$key;
    isClient?: boolean;
    isThinPage: boolean;
    relay: RelayProp;
    saveSearchTopButton?: ReactNode;
};

export const SbRespMainContent: FC<Props> = ({
    currency,
    itemSearch: itemSearchRef,
    auctionsItemSearch: auctionsItemSearchRef,
    viewer: viewerRef,
    user: userRef,
    isClient,
    isThinPage,
    relay,
    saveSearchTopButton,
}) => {
    const { showMeasurements, generatedUriRef, page } = useSbSelector(
        ({ displayPreferences, filters }) => ({
            showMeasurements: displayPreferences.showMeasurements,
            generatedUriRef: filters.generatedUriRef,
            page: filters.page,
        })
    );
    const { isNewNetPrice15Filter } = useServerVarsContext();
    const viewer = useFragment(viewerFragment, viewerRef);
    const user = useFragment(userFragment, userRef);
    const itemSearch = useFragment(itemSearchFragment, itemSearchRef);
    const auctionsItemSearch = useFragment(auctionsItemSearchFragment, auctionsItemSearchRef);

    const { topCategoryL1, displayUriRef, displayMaxNumberOfPages, pageType } = itemSearch;

    const { canShowInternalSellerName, canShowSellerName, canShowStorefrontOnlyBadge } = user || {};

    const isFurniture = topCategoryL1 === VERTICALS.FURNITURE;
    const canShowCreatorName = !canShowSellerName && isFurniture;

    const sharedProps = {
        currency,
        itemSearch,
        viewer,
        user,
        isClient,
    };

    return (
        <div className={classnames(dibsCss.flex, dibsCss.flexNowrap)}>
            <SbRespRefineMenuSelector
                {...sharedProps}
                uriRef={generatedUriRef || ''}
                isThinPage={isThinPage}
            />
            <div className={classnames(dibsCss.flexGrow, dibsCss.minW1px)}>
                <div
                    className={classnames(
                        dibsCss.flex,
                        dibsCss.justifyBetween,
                        dibsCss.flexRowReverse,
                        dibsCss.mbLarge,
                        dibsCss.gapSmall
                    )}
                >
                    <div className={classnames(dibsCss.flex, dibsCss.justifyEnd, dibsCss.itemsEnd)}>
                        <SbRespSortItems itemSearch={itemSearch} user={user} />
                        <SbRespPageSize itemSearch={itemSearch} />
                    </div>
                    <div className={isNewNetPrice15Filter ? dibsCss.myAuto : ''}>
                        <SbRespBrowseHeader itemSearch={itemSearch} />
                        {isNewNetPrice15Filter ? (
                            <SbSharedNetPriceFilterSwitch itemSearch={itemSearch} />
                        ) : (
                            saveSearchTopButton
                        )}
                    </div>
                </div>
                <SbRespAppliedFilters user={user} currency={currency} itemSearch={itemSearch} />
                <div className={classnames(dibsCss.flexGrow, dibsCss.minW1px)}>
                    {/* Item Results */}
                    <SbRespSearchResultContainer
                        {...sharedProps}
                        showInternalSellerName={canShowInternalSellerName}
                        showStorefrontOnlyBadge={
                            canShowStorefrontOnlyBadge && pageType === pageTypes.DEALER
                        }
                        showSellerName={!!canShowSellerName}
                        showCreatorName={canShowCreatorName}
                        showMeasurements={showMeasurements}
                        relay={relay}
                        auctionsItemSearch={auctionsItemSearch}
                    />
                    {/* Related Items */}
                    {isThinPage && (
                        <>
                            <SbSharedRelatedItems viewer={viewer} itemSearch={itemSearch} />
                            <SbSharedRecentlySoldItems viewer={viewer} itemSearch={itemSearch} />
                        </>
                    )}

                    {/* Pagination */}
                    <div className={classnames(dibsCss.my0, dibsCss.mxAuto)}>
                        <SharedPagination
                            currentPage={page}
                            numberOfPages={displayMaxNumberOfPages ?? 0}
                            displayUriRef={displayUriRef || ''}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};
